import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { Building, User, MapPin, CreditCard, UserCheck, FileText, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { NextStepPrompt } from './NextStepPrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('token');

const api = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
};

export function EnhancedLeadForm({ leadId, onSave, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [showNextSteps, setShowNextSteps] = useState(false);
  const [savedLeadId, setSavedLeadId] = useState(null);
  const [formData, setFormData] = useState({
    // Company Information
    company_name: '',
    facility_name: '',
    
    // Contact Information
    contact_name: '',
    contact_title: '',
    email: '',
    phone: '',
    
    // Service Address
    service_address_line_1: '',
    service_address_line_2: '',
    city: '',
    state: '',
    zip: '',
    
    // Billing Address
    billing_address_same_as_service: true,
    billing_address_line_1: '',
    billing_address_line_2: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    
    // Decision Maker
    decision_maker_name: '',
    decision_maker_title: '',
    
    // Lead Management
    preferred_contact_method: 'email',
    source: 'direct',
    status: 'new',
    notes: ''
  });

  useEffect(() => {
    if (leadId) {
      loadLead();
    }
  }, [leadId]);

  const loadLead = async () => {
    try {
      const data = await api(`/api/leads/${leadId}`);
      setFormData(data);
    } catch (error) {
      toast.error('Failed to load lead');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!formData.company_name || !formData.contact_name) {
      toast.error('Company name and contact name are required');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (leadId) {
        // Update existing lead
        response = await api(`/api/leads/${leadId}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        toast.success('Lead updated successfully');
        setSavedLeadId(leadId);
      } else {
        // Create new lead
        response = await api('/api/leads', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        toast.success('Lead created successfully');
        setSavedLeadId(response.id);
      }
      
      // Show next steps prompt
      setShowNextSteps(true);
    } catch (error) {
      toast.error('Failed to save lead');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {showNextSteps && (
        <NextStepPrompt
          title="Lead Saved Successfully!"
          message="What would you like to do next?"
          primaryAction="Start Walkthrough"
          secondaryAction="Close"
          onPrimaryClick={() => {
            setShowNextSteps(false);
            if (onSave) onSave(savedLeadId, 'start-walkthrough');
          }}
          onSecondaryClick={() => {
            setShowNextSteps(false);
            if (onSave) onSave(savedLeadId);
          }}
        />
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-[Space_Grotesk] font-semibold">
          {leadId ? 'Edit Lead' : 'New Lead'}
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading} data-testid="save-lead-button">
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : 'Save Lead'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="company" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="company">
            <Building className="h-4 w-4 mr-2" />
            Company
          </TabsTrigger>
          <TabsTrigger value="contact">
            <User className="h-4 w-4 mr-2" />
            Contact
          </TabsTrigger>
          <TabsTrigger value="service-address">
            <MapPin className="h-4 w-4 mr-2" />
            Service Address
          </TabsTrigger>
          <TabsTrigger value="billing">
            <CreditCard className="h-4 w-4 mr-2" />
            Billing
          </TabsTrigger>
          <TabsTrigger value="decision-maker">
            <UserCheck className="h-4 w-4 mr-2" />
            Decision Maker
          </TabsTrigger>
          <TabsTrigger value="notes">
            <FileText className="h-4 w-4 mr-2" />
            Notes
          </TabsTrigger>
        </TabsList>

        {/* Company Tab */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  placeholder="ABC Corporation"
                  data-testid="company-name-input"
                />
              </div>
              <div>
                <Label htmlFor="facility_name">Facility Name</Label>
                <Input
                  id="facility_name"
                  value={formData.facility_name}
                  onChange={(e) => handleChange('facility_name', e.target.value)}
                  placeholder="Corporate Headquarters"
                  data-testid="facility-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="source">Lead Source</Label>
                  <select
                    id="source"
                    className="w-full h-10 px-3 rounded-md border border-gray-300"
                    value={formData.source}
                    onChange={(e) => handleChange('source', e.target.value)}
                    data-testid="lead-source-select"
                  >
                    <option value="direct">Direct</option>
                    <option value="referral">Referral</option>
                    <option value="web">Web</option>
                    <option value="cold_call">Cold Call</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    className="w-full h-10 px-3 rounded-md border border-gray-300"
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    data-testid="lead-status-select"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="proposal_sent">Proposal Sent</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Tab */}
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => handleChange('contact_name', e.target.value)}
                    placeholder="John Smith"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_title">Contact Title</Label>
                  <Input
                    id="contact_title"
                    value={formData.contact_title}
                    onChange={(e) => handleChange('contact_title', e.target.value)}
                    placeholder="Facilities Manager"
                    data-testid="contact-title-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="john.smith@company.com"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="preferred_contact_method">Preferred Contact Method</Label>
                <select
                  id="preferred_contact_method"
                  className="w-full h-10 px-3 rounded-md border border-gray-300"
                  value={formData.preferred_contact_method}
                  onChange={(e) => handleChange('preferred_contact_method', e.target.value)}
                  data-testid="preferred-contact-select"
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="text">Text</option>
                </select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Service Address Tab */}
        <TabsContent value="service-address">
          <Card>
            <CardHeader>
              <CardTitle>Service Address</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="service_address_line_1">Address Line 1</Label>
                <Input
                  id="service_address_line_1"
                  value={formData.service_address_line_1}
                  onChange={(e) => handleChange('service_address_line_1', e.target.value)}
                  placeholder="123 Main Street"
                  data-testid="service-address-1-input"
                />
              </div>
              <div>
                <Label htmlFor="service_address_line_2">Address Line 2</Label>
                <Input
                  id="service_address_line_2"
                  value={formData.service_address_line_2}
                  onChange={(e) => handleChange('service_address_line_2', e.target.value)}
                  placeholder="Suite 100"
                  data-testid="service-address-2-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Denver"
                    data-testid="city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    placeholder="CO"
                    maxLength={2}
                    data-testid="state-input"
                  />
                </div>
                <div>
                  <Label htmlFor="zip">ZIP Code</Label>
                  <Input
                    id="zip"
                    value={formData.zip}
                    onChange={(e) => handleChange('zip', e.target.value)}
                    placeholder="80202"
                    data-testid="zip-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Address Tab */}
        <TabsContent value="billing">
          <Card>
            <CardHeader>
              <CardTitle>Billing Address</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="billing_same"
                  checked={formData.billing_address_same_as_service}
                  onCheckedChange={(checked) => handleChange('billing_address_same_as_service', checked)}
                  data-testid="billing-same-checkbox"
                />
                <Label htmlFor="billing_same">Same as service address</Label>
              </div>

              {!formData.billing_address_same_as_service && (
                <>
                  <div>
                    <Label htmlFor="billing_address_line_1">Address Line 1</Label>
                    <Input
                      id="billing_address_line_1"
                      value={formData.billing_address_line_1}
                      onChange={(e) => handleChange('billing_address_line_1', e.target.value)}
                      placeholder="123 Billing Street"
                      data-testid="billing-address-1-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="billing_address_line_2">Address Line 2</Label>
                    <Input
                      id="billing_address_line_2"
                      value={formData.billing_address_line_2}
                      onChange={(e) => handleChange('billing_address_line_2', e.target.value)}
                      placeholder="Suite 200"
                      data-testid="billing-address-2-input"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="billing_city">City</Label>
                      <Input
                        id="billing_city"
                        value={formData.billing_city}
                        onChange={(e) => handleChange('billing_city', e.target.value)}
                        placeholder="Denver"
                        data-testid="billing-city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="billing_state">State</Label>
                      <Input
                        id="billing_state"
                        value={formData.billing_state}
                        onChange={(e) => handleChange('billing_state', e.target.value)}
                        placeholder="CO"
                        maxLength={2}
                        data-testid="billing-state-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="billing_zip">ZIP Code</Label>
                      <Input
                        id="billing_zip"
                        value={formData.billing_zip}
                        onChange={(e) => handleChange('billing_zip', e.target.value)}
                        placeholder="80202"
                        data-testid="billing-zip-input"
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Decision Maker Tab */}
        <TabsContent value="decision-maker">
          <Card>
            <CardHeader>
              <CardTitle>Decision Maker</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="decision_maker_name">Name</Label>
                  <Input
                    id="decision_maker_name"
                    value={formData.decision_maker_name}
                    onChange={(e) => handleChange('decision_maker_name', e.target.value)}
                    placeholder="Jane Doe"
                    data-testid="decision-maker-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="decision_maker_title">Title</Label>
                  <Input
                    id="decision_maker_title"
                    value={formData.decision_maker_title}
                    onChange={(e) => handleChange('decision_maker_title', e.target.value)}
                    placeholder="COO"
                    data-testid="decision-maker-title-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <Card>
            <CardHeader>
              <CardTitle>Notes & Additional Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={8}
                  placeholder="Additional notes about this lead..."
                  data-testid="notes-textarea"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
